import clsx from 'clsx';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Core/Icon';
import FileIcon from '../FileIcon';
import { formatFileSize } from '../../../utils/utils';
import { FileUploadStatusEnum } from '../../../enums/FileUploadStatusEnum';
import type GroupedUploadItem from '../../../interfaces/GroupedUploadItem';
import { useFileUpload } from '../../../hooks/useFileUpload';
import TruncatedTooltip from '../TruncatedTooltip';
import { Button, Spin } from 'antd';

interface Props {
  files: GroupedUploadItem[];
  handleRemoveFile: (file: GroupedUploadItem) => void;
}

const QuickShareFilesList = ({ files, handleRemoveFile }: Props) => {
  const { t } = useTranslation();
  const { isUploadingQuickShareFiles } = useFileUpload();
  const { handleOpenUploadingFilesErrorsModal } = useFileUpload();
  return (
    <div
      className={clsx(
        'divide-gray my-2 h-full max-h-52 w-full divide-y overflow-auto border-b',
        files.length !== 0 ? 'border-gray' : 'border-transparent',
      )}
    >
      {files.map((file, index) => (
        <div
          key={index}
          className="flex h-10 w-full flex-row items-center justify-between gap-2 overflow-hidden px-2"
        >
          <div className="flex items-center">
            <div className="flex min-w-0 flex-row items-center gap-1">
              <FileIcon
                fileExtension={file.fileExtension || ''}
                isFolder={file.isFolder}
                className="h-4 w-4 shrink-0"
              />
              <h2 className="text-blue-dark max-w-64 font-semibold">
                <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
              </h2>
            </div>
          </div>

          <div className="flex shrink-0 flex-row items-center justify-center gap-2">
            <div className="text-gray-dark text-center align-baseline text-sm leading-none">
              {formatFileSize(file.fileSize)}
            </div>

            {((): ReactNode => {
              switch (file.status) {
                case FileUploadStatusEnum.Pending:
                  return (
                    <>
                      {!isUploadingQuickShareFiles && (
                        <Button
                          type="text"
                          aria-label={t('a11y.removeFile')}
                          disabled={isUploadingQuickShareFiles}
                          onClick={() => handleRemoveFile(file)}
                          className="text-gray-dark p-0 text-sm"
                        >
                          <Icon name="XIcon" className="h-5 w-5" />
                        </Button>
                      )}
                      {isUploadingQuickShareFiles && (
                        <>
                          <Icon
                            name={file.isFolder ? 'FolderUpIcon' : 'FileUpIcon'}
                            className="text-azure h-4 w-4"
                          />
                          <Spin size="small" percent={file.progress} />
                          <span className="w-9 text-sm">{`${file.progress}%`}</span>
                        </>
                      )}
                    </>
                  );
                case FileUploadStatusEnum.Uploading:
                  return (
                    <>
                      <Icon
                        name={file.isFolder ? 'FolderUpIcon' : 'FileUpIcon'}
                        className="text-azure h-4 w-4"
                      />
                      <Spin size="small" percent={file.progress} />
                      <span className="w-9 text-sm">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.Failed:
                  return (
                    <>
                      <Button
                        type="text"
                        aria-label={t('a11y.viewUploadErrors')}
                        className="p-0"
                        onClick={() => {
                          handleOpenUploadingFilesErrorsModal(true);
                        }}
                      >
                        <Icon name="CircleXIcon" className="text-red h-4 w-4" />
                      </Button>
                      <Spin size="small" percent={file.progress} />
                      <span className="text-blue-dark w-9 text-sm">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.FailedSharing:
                  return (
                    <>
                      <Icon name="TriangleAlertIcon" className="text-yellow-dark h-4 w-4" />
                      <Spin size="small" percent={file.progress} />
                      <span className="text-blue-dark w-9 text-sm">{`${file.progress}%`}</span>
                    </>
                  );
                case FileUploadStatusEnum.Completed:
                  return (
                    <>
                      <Icon name="CircleCheckIcon" className="text-green h-4 w-4" />
                      <Spin size="small" percent={file.progress} />
                      <span className="text-blue-dark w-9 text-sm">{`${file.progress}%`}</span>
                    </>
                  );
                default:
                  return 'N/D';
              }
            })()}
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuickShareFilesList;
